import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  addEvent,
  updateEvent,
  deleteEvent,
  getEventsBySection,
} from "../../../../../services/eventsServices";

import {
  buildEventPayload,
  filterEvents,
  getAgendaStats,
  getFallbackSectionId,
  isDemoMode,
  sortEvents,
} from "../utils/agendaUtils";

export default function useAgenda(sectionId) {
  const finalSectionId = useMemo(
    () => getFallbackSectionId(sectionId),
    [sectionId]
  );

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("all");

  const [drawer, setDrawer] = useState({
    type: null,
    event: null,
  });

  const demoMode = useMemo(() => isDemoMode(), []);

  const fetchEvents = useCallback(async () => {
    if (!finalSectionId) {
      setEvents([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getEventsBySection(finalSectionId);
      const safeData = Array.isArray(data) ? data : [];

      setEvents(sortEvents(safeData));
    } catch (error) {
      console.error("Erreur lors de la récupération des événements :", error);
      toast.error("Erreur lors de la récupération des événements.");
    } finally {
      setLoading(false);
    }
  }, [finalSectionId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const stats = useMemo(() => getAgendaStats(events), [events]);

  const rows = useMemo(() => {
    return filterEvents(events, searchTerm, statusFilter, periodFilter);
  }, [events, searchTerm, statusFilter, periodFilter]);

  const handleDemoClick = () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return true;
    }

    return false;
  };

  const openCreateDrawer = () => {
    if (handleDemoClick()) return;

    setDrawer({
      type: "create",
      event: null,
    });
  };

  const openEditDrawer = (event) => {
    if (handleDemoClick()) return;

    setDrawer({
      type: "edit",
      event,
    });
  };

  const closeDrawer = () => {
    setDrawer({
      type: null,
      event: null,
    });
  };

  const handleCreate = async (formData) => {
    if (handleDemoClick()) return;

    try {
      setSaving(true);

      const payload = buildEventPayload(formData, finalSectionId);

      await addEvent(payload);

      toast.success("Événement créé avec succès.");
      await fetchEvents();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la création de l'événement :", error);
      toast.error("Erreur lors de la création de l'événement.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (eventId, formData, originalEvent) => {
    if (handleDemoClick()) return;

    try {
      setSaving(true);

      const payload = {
        ...originalEvent,
        ...buildEventPayload(formData, finalSectionId),
        id: eventId,
      };

      await updateEvent(payload);

      toast.success("Événement modifié avec succès.");
      await fetchEvents();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la modification de l'événement :", error);
      toast.error("Erreur lors de la modification de l'événement.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (handleDemoClick()) return;

    const confirmed = window.confirm("Supprimer cet événement ?");

    if (!confirmed) return;

    try {
      await deleteEvent(eventId);

      setEvents((prev) => prev.filter((event) => event.id !== eventId));

      toast.success("Événement supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression de l'événement :", error);
      toast.error("Erreur lors de la suppression de l'événement.");
    }
  };

  return {
    finalSectionId,
    events,
    rows,
    stats,
    loading,
    saving,
    demoMode,

    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    periodFilter,
    setPeriodFilter,

    drawer,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,

    fetchEvents,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
}